import { m } from "@recobra/i18n/messages";
import type {
  NotificationDisplayParams,
  NotificationInboxItem,
} from "@recobra/schemas/notification";

import type { TaskName } from "../tasks";
import type { FormattedNotificationContent } from "./formatNotificationContent";

const taskLabel = (
  params: NotificationDisplayParams | undefined,
  fallback: string,
  taskName: TaskName,
): string => {
  const name = params?.taskType ? taskName(params.taskType) : undefined;
  const resolved = name !== undefined && name !== params?.taskType;
  return resolved ? name : (params?.taskTitle ?? name ?? fallback);
};

export const caseNumberLabel = (item: NotificationInboxItem): string | undefined => {
  if (item.displayParams?.caseNumber != null) {
    return String(item.displayParams.caseNumber);
  }

  if (item.link.type === "case" && item.link.caseNumber != null) {
    return String(item.link.caseNumber);
  }

  return undefined;
};

export const stepLabel = (step: string | undefined, fallback: string): string => {
  if (!step) return fallback;

  return m.case_step_label({ step: step as never });
};

export const formatTaskNotification = (
  item: NotificationInboxItem,
  kind:
    | "task.assigned"
    | "task.completed"
    | "task.canceled"
    | "task.reopened"
    | "task.sla_breach"
    | "task.reminder",
  taskName: TaskName,
): FormattedNotificationContent | null => {
  const params = item.displayParams;
  const caseNumber = caseNumberLabel(item);
  const task = taskLabel(params, item.title, taskName);

  switch (kind) {
    case "task.assigned":
      if (!caseNumber) return null;
      return {
        title: m.notifications_task_assigned_title({ task }),
        message: m.notifications_task_assigned_body({ caseNumber }),
        actionLabel: m.notifications_action_view_task(),
      };
    case "task.canceled":
    case "task.reopened":
      return {
        title:
          kind === "task.canceled"
            ? m.notifications_task_canceled_title({ task })
            : m.notifications_task_reopened_title({ task }),
        message: m.notifications_task_transition_body(),
        actionLabel: m.notifications_action_view_task(),
      };
    case "task.completed":
      if (!caseNumber) return null;
      if (params?.completionAudience === "creator") {
        return {
          title: m.notifications_task_completed_creator_title(),
          message: m.notifications_task_completed_creator_body({ caseNumber }),
          actionLabel: m.notifications_action_view_task(),
        };
      }
      if (params?.completionAudience === "open_task") {
        return {
          title: m.notifications_task_completed_holder_title(),
          message: m.notifications_task_completed_holder_body({ caseNumber }),
          actionLabel: m.notifications_action_view_task(),
        };
      }
      return {
        title: m.notifications_task_completed_title({ task }),
        message: m.notifications_task_completed_body({ caseNumber }),
        actionLabel: m.notifications_action_view_task(),
      };
    case "task.sla_breach":
      if (!caseNumber) return null;
      return {
        title: m.notifications_task_sla_breach_title({ task }),
        message: m.notifications_task_sla_breach_body({ caseNumber }),
        actionLabel: m.notifications_action_view_task(),
      };
    case "task.reminder":
      return {
        title: m.notifications_task_reminder_title({ task }),
        message: params?.reminderComment ?? m.notifications_task_reminder_body(),
        actionLabel: m.notifications_action_view_task(),
      };
  }
};
